import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Badge, Button, Modal, Steps, Radio, Checkbox, Rate, Input, Progress, Carousel, Avatar, Tooltip } from 'antd';
import {
  BellOutlined,
  CloseOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  FileTextOutlined,
  RightOutlined,
  LeftOutlined,
  ExpandOutlined,
  CompressOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined
} from '@ant-design/icons';

import './notification.css';
import avataricon from '../../assets/images/avvatar1.jpg';
const { TextArea } = Input;
const { Step } = Steps;

const STORAGE_PREFIX = 'notification_widget:';

const storageKey = (id) => `${STORAGE_PREFIX}${String(id)}`;

const safeJsonParse = (s, fallback) => {
  if (!s) return fallback;
  try {
    const v = JSON.parse(s);
    return (v === null || typeof v === 'undefined') ? fallback : v;
  } catch (e) {
    return fallback;
  }
};

const getStoredItemState = (id) => {
  if (typeof sessionStorage === 'undefined') return null;
  return safeJsonParse(sessionStorage.getItem(storageKey(id)), null);
};

const setStoredItemState = (id, patch) => {
  if (typeof sessionStorage === 'undefined') return;
  const prev = getStoredItemState(id) || {};
  const next = { ...prev, ...patch, updatedAt: Date.now() };
  sessionStorage.setItem(storageKey(id), JSON.stringify(next));
};

const getTrainingSteps = (item) => {
  if (!item || typeof item !== 'object') return [];

  if (Array.isArray(item.steps) && item.steps.length > 0) {
    return item.steps.map((s, idx) => {
      if (!s || typeof s !== 'object') {
        return { id: `step_${idx}`, type: 'content', title: `Step ${idx + 1}` };
      }
      const type = (s.type || (s.videoUrl ? 'video' : 'content'));
      return {
        id: s.id || `step_${idx}`,
        type,
        title: s.title || `Step ${idx + 1}`,
        subtitle: s.subtitle,
        description: s.description,
        content: s.content,
        videoUrl: s.videoUrl,
        questions: s.questions,
        passingScore: s.passingScore,
        required: s.required,
      };
    });
  }

  if (item.videoUrl) {
    return [
      {
        id: 'video',
        type: 'video',
        title: item.title || 'Training',
        subtitle: item.subtitle,
        description: item.description,
        videoUrl: item.videoUrl,
      },
    ];
  }

  return [];
};

// Sample data structure
const sampleContent = [
  {
    id: 1,
    type: 'notification',
    title: 'System Update Available',
    message: 'A new version of the system is ready to install.',
    priority: 'high',
    read: false,
    timestamp: new Date().toISOString()
  },
  {
    id: 2,
    type: 'survey',
    title: 'Employee Satisfaction Survey',
    description: 'Help us improve by sharing your feedback',
    priority: 'medium',
    read: false,
    required: true,
    questions: [
      {
        id: 'q1',
        question: 'How satisfied are you with your work environment?',
        type: 'rating',
        required: true
      },
      {
        id: 'q2',
        question: 'What could we improve?',
        type: 'textarea',
        required: false
      }
    ]
  },
  {
    id: 3,
    type: 'quiz',
    title: 'Security Awareness Quiz',
    description: 'Test your knowledge on cybersecurity best practices',
    priority: 'high',
    read: false,
    required: true,
    passingScore: 70,
    questions: [
      {
        id: 'quiz1',
        question: 'What is phishing?',
        type: 'multiple-choice',
        options: [
          'A type of fish',
          'A fraudulent attempt to obtain sensitive information',
          'A computer virus',
          'A software update'
        ],
        correctAnswer: 1
      },
      {
        id: 'quiz2',
        question: 'Which of these are strong password practices?',
        type: 'multiple-select',
        options: [
          'Use the same password everywhere',
          'Include numbers and symbols',
          'Make it at least 12 characters',
          'Share it with colleagues'
        ],
        correctAnswers: [1, 2]
      }
    ]
  },
  {
    id: 4,
    type: 'training',
    title: 'New Features Training',
    description: 'Learn about the latest features in our platform',
    priority: 'medium',
    read: false,
    required: false,
    steps: [
      {
        title: 'Introduction',
        content: 'Welcome to the new features training module.',
        videoUrl: 'https://www.loom.com/embed/ac861547634e4b5e92d426c445ee4dee'
      },
      {
        title: 'Dashboard Updates',
        content: 'The dashboard now includes real-time analytics and customizable widgets.',
        videoUrl: 'https://www.loom.com/embed/ac861547634e4b5e92d426c445ee4dee'
      },
      {
        title: 'Advanced Reporting',
        content: 'Generate detailed reports with our new reporting engine.',
        videoUrl: 'https://www.loom.com/embed/ac861547634e4b5e92d426c445ee4dee'
      }
    ]
  }
];

const NotificationWidget = ({
  content = sampleContent,
  onComplete,
  forcePopup = false,
  position = 'right',
  maxForcedCloses = 3,
  viewMode = 'list',
  defaultExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(!!defaultExpanded);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [activeType, setActiveType] = useState('all');
  const [items, setItems] = useState(() => {
    const base = Array.isArray(content) ? content : [];
    return base.map((it) => {
      const st = it && typeof it === 'object' ? getStoredItemState(it.id) : null;
      if (!st) return it;
      return {
        ...it,
        read: typeof st.read === 'boolean' ? st.read : it.read,
      };
    });
  });
  const [surveyAnswers, setSurveyAnswers] = useState({});
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [isWidgetModalOpen, setIsWidgetModalOpen] = useState(false);
  const carouselRef = useRef(null);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [previewMessage, setPreviewMessage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const previewTimeoutRef = useRef(null);
  const messageQueueRef = useRef([]);
  const [videoExpanded, setVideoExpanded] = useState(false);
  const [videoFullscreen, setVideoFullscreen] = useState(false);
  const videoContainerRef = useRef(null);

  const requiredUnread = useMemo(
    () => items.filter((item) => item && item.required && !item.read),
    [items]
  );

  useEffect(() => {
    if (!forcePopup) return;
    if (showModal) return;
    if (selectedItem) return;
    if (requiredUnread.length <= 0) return;

    const candidate = requiredUnread.find((it) => {
      const st = getStoredItemState(it.id) || {};
      if (st.completed) return false;
      if (typeof st.dismissCount === 'number' && st.dismissCount >= maxForcedCloses) {
        return true;
      }
      if (st.shownThisSession) return false;
      return true;
    }) || requiredUnread[0];

    if (candidate) {
      setStoredItemState(candidate.id, { shownThisSession: true });
      setSelectedItem(candidate);
      setCurrentStep(0);
      setSurveyAnswers({});
      setQuizAnswers({});
      setShowModal(true);
    }
  }, [forcePopup, items, showModal, selectedItem, requiredUnread, maxForcedCloses]);

  useEffect(() => {
    if (viewMode !== 'slider') return;
    if (!isExpanded) return;

    const base = activeType === 'all' ? items : items.filter((x) => x && x.type === activeType);
    const req = base.filter((x) => x && x.required && !x.read);
    const unread = base.filter((x) => x && !x.read);
    const list = req.length > 0 ? req : (unread.length > 0 ? unread : base);
    const it = list[0];
    if (!it) return;

    setSliderIndex(0);
    resetItemStateForView(it);
  }, [isExpanded, viewMode, items, activeType]);

  // Random notification preview effect
  useEffect(() => {
    if (isExpanded) {
      // Clear preview when widget is expanded
      setShowPreview(false);
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
      return;
    }

    const unreadItems = items.filter(item => !item.read);
    if (unreadItems.length === 0) return;

    // Initialize message queue if empty
    if (messageQueueRef.current.length === 0) {
      messageQueueRef.current = [...unreadItems];
    }

    const showRandomPreview = () => {
      if (messageQueueRef.current.length === 0) {
        messageQueueRef.current = [...unreadItems];
      }

      // Get random item from queue
      const randomIndex = Math.floor(Math.random() * messageQueueRef.current.length);
      const randomItem = messageQueueRef.current[randomIndex];
      
      // Remove from queue
      messageQueueRef.current.splice(randomIndex, 1);

      setPreviewMessage({
        title: randomItem.title,
        description: randomItem.description || randomItem.message,
        type: randomItem.type,
        priority: randomItem.priority
      });
      setShowPreview(true);

      // Hide preview after 4 seconds
      setTimeout(() => {
        setShowPreview(false);
      }, 4000);

      // Show next preview after random interval (8-15 seconds)
      const nextInterval = Math.random() * 7000 + 8000;
      previewTimeoutRef.current = setTimeout(showRandomPreview, nextInterval);
    };

    // Start showing previews after initial delay (5-8 seconds)
    const initialDelay = Math.random() * 3000 + 5000;
    previewTimeoutRef.current = setTimeout(showRandomPreview, initialDelay);

    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, [isExpanded, items]);

  const unreadCount = items.filter(item => !item.read).length;
  const requiredCount = items.filter(item => item.required && !item.read).length;

  const markAsRead = (itemId) => {
    setItems(items.map(item =>
      item.id === itemId ? { ...item, read: true } : item
    ));
    setStoredItemState(itemId, { read: true, completed: true });
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setCurrentStep(0);
    setSurveyAnswers({});
    setQuizAnswers({});
    setShowModal(true);
    setVideoExpanded(false);
    setVideoFullscreen(false);
    setStoredItemState(item.id, { shownThisSession: true });
  };

  const resetItemStateForView = (item) => {
    if (!item) return;
    setSelectedItem(item);
    setCurrentStep(0);
    setSurveyAnswers({});
    setQuizAnswers({});
    setVideoExpanded(false);
    setVideoFullscreen(false);
    setStoredItemState(item.id, { shownThisSession: true });
  };

  const handleClose = (override) => {
    const target = (override && typeof override === 'object' && typeof override.id !== 'undefined') ? override : selectedItem;
    if (!target) {
      setShowModal(false);
      setSelectedItem(null);
      setVideoExpanded(false);
      setVideoFullscreen(false);
      return;
    }

    const st = getStoredItemState(target.id) || {};
    const dismissCount = typeof st.dismissCount === 'number' ? st.dismissCount : 0;
    const steps = getTrainingSteps(target);
    const mustComplete = target.required && !target.read && dismissCount >= maxForcedCloses;

    if (mustComplete) {
      Modal.warning({
        title: 'Action Required',
        content: steps.length > 0
          ? 'You have reached the maximum number of closes. Please complete all steps to continue.'
          : 'You have reached the maximum number of closes. Please complete this item to continue.',
      });
      return;
    }

    if (target.required && !target.read) {
      Modal.confirm({
        title: 'Required Item',
        content: `This item is required. You can close it ${Math.max(0, maxForcedCloses - dismissCount)} more time(s) before completion is enforced. Close now?`,
        onOk: () => {
          setStoredItemState(target.id, { dismissCount: dismissCount + 1 });
          setShowModal(false);
          setSelectedItem(null);
          setVideoExpanded(false);
          setVideoFullscreen(false);
        }
      });
      return;
    }

    setShowModal(false);
    setSelectedItem(null);
    setVideoExpanded(false);
    setVideoFullscreen(false);
  };

  const toggleVideoFullscreen = () => {
    if (!videoContainerRef.current) return;
    
    if (!videoFullscreen) {
      if (videoContainerRef.current.requestFullscreen) {
        videoContainerRef.current.requestFullscreen();
      } else if (videoContainerRef.current.webkitRequestFullscreen) {
        videoContainerRef.current.webkitRequestFullscreen();
      } else if (videoContainerRef.current.mozRequestFullScreen) {
        videoContainerRef.current.mozRequestFullScreen();
      }
      setVideoFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      }
      setVideoFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setVideoFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const getFilteredItems = () => {
    return activeType === 'all' ? items : items.filter((x) => x && x.type === activeType);
  };

  const getSliderItems = () => {
    const base = getFilteredItems();
    const req = base.filter((x) => x && x.required && !x.read);
    const unread = base.filter((x) => x && !x.read);
    return req.length > 0 ? req : (unread.length > 0 ? unread : base);
  };

  const closeCurrentSlide = () => {
    const list = getSliderItems();
    const it = list[sliderIndex] || list[0];
    if (!it) {
      setIsExpanded(false);
      return;
    }
    handleClose(it);
  };

  const handleSurveySubmit = () => {
    markAsRead(selectedItem.id);
    if (onComplete) {
      onComplete({
        type: 'survey',
        itemId: selectedItem.id,
        answers: surveyAnswers
      });
    }
    setShowModal(false);
    setSelectedItem(null);
  };

  const handleQuizSubmit = () => {
    const { questions, passingScore } = selectedItem;
    let correctCount = 0;

    questions.forEach((q) => {
      const userAnswer = quizAnswers[q.id];
      if (q.type === 'multiple-choice') {
        if (userAnswer === q.correctAnswer) correctCount++;
      } else if (q.type === 'multiple-select') {
        const userSet = new Set(userAnswer || []);
        const correctSet = new Set(q.correctAnswers);
        if (userSet.size === correctSet.size && 
            [...userSet].every(val => correctSet.has(val))) {
          correctCount++;
        }
      }
    });

    const score = (correctCount / questions.length) * 100;
    const passed = score >= passingScore;

    if (passed) {
      markAsRead(selectedItem.id);
    }

    if (onComplete) {
      onComplete({
        type: 'quiz',
        itemId: selectedItem.id,
        score,
        passed,
        answers: quizAnswers
      });
    }

    Modal.info({
      title: passed ? '🎉 Quiz Passed!' : '📚 Quiz Not Passed',
      content: `You scored ${score.toFixed(0)}%. ${passed ? 'Great job!' : `You need ${passingScore}% to pass. Please review and try again.`}`,
      onOk: () => {
        if (passed) {
          setShowModal(false);
          setSelectedItem(null);
        } else {
          setQuizAnswers({});
        }
      }
    });
  };

  const handleTrainingComplete = () => {
    markAsRead(selectedItem.id);
    if (onComplete) {
      onComplete({
        type: 'training',
        itemId: selectedItem.id,
        completed: true
      });
    }
    setShowModal(false);
    setSelectedItem(null);
  };

  const markTrainingStepComplete = (itemId, stepId) => {
    const st = getStoredItemState(itemId) || {};
    const done = Array.isArray(st.stepsCompleted) ? st.stepsCompleted : [];
    if (!done.includes(stepId)) {
      setStoredItemState(itemId, { stepsCompleted: [...done, stepId] });
    }
  };

  const isTrainingComplete = (item) => {
    const steps = getTrainingSteps(item);
    if (steps.length <= 0) return true;
    const st = getStoredItemState(item.id) || {};
    const done = Array.isArray(st.stepsCompleted) ? st.stepsCompleted : [];
    return steps.every((s) => done.includes(s.id));
  };

  const renderVideo = (step, options = {}) => {
    const { allowExpand = true, allowFullscreen = true } = options;
    
    if (!step?.videoUrl) return null;

    const paddingBottom = videoExpanded ? '56.25%' : '45%';

    return (
      <div 
        ref={videoContainerRef}
        style={{ 
          position: 'relative', 
          paddingBottom, 
          height: 0,
          backgroundColor: '#000',
          borderRadius: '8px',
          overflow: 'hidden',
          transition: 'padding-bottom 0.3s ease'
        }}
      >
        <iframe
          style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%',
            border: 'none'
          }}
          src={step.videoUrl}
          title={step.title || 'Training Video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
        <div 
          style={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            display: 'flex',
            gap: 8,
            zIndex: 10
          }}
        >
          {allowExpand && (
            <Tooltip title={videoExpanded ? "Collapse" : "Expand"}>
              <Button
                size="small"
                shape="circle"
                icon={videoExpanded ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={() => setVideoExpanded(!videoExpanded)}
                style={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  borderColor: 'transparent',
                  color: 'white'
                }}
              />
            </Tooltip>
          )}
          {allowFullscreen && (
            <Tooltip title={videoFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
              <Button
                size="small"
                shape="circle"
                icon={videoFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={toggleVideoFullscreen}
                style={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  borderColor: 'transparent',
                  color: 'white'
                }}
              />
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  const renderNotification = (item) => (
    <div className="notification-content">
      <div className="notification-header">
        <h3>{item.title}</h3>
      </div>
      <p className="notification-message">{item.message}</p>
      <div className="notification-actions">
        <Button 
          type="primary" 
          onClick={() => {
            markAsRead(item.id);
            setShowModal(false);
            setSelectedItem(null);
          }}
          block
        >
          Got it
        </Button>
      </div>
    </div>
  );

  const renderSurvey = (item) => (
    <div className="survey-content">
      <div className="survey-header">
        <h3>{item.title}</h3>
        <p className="survey-description">{item.description}</p>
      </div>
      
      <div className="survey-questions">
        {item.questions.map((q) => (
          <div key={q.id} className="survey-question">
            <label className="question-label">
              {q.question}
              {q.required && <span className="required-mark">*</span>}
            </label>
            
            {q.type === 'rating' && (
              <Rate 
                value={surveyAnswers[q.id]} 
                onChange={(value) => setSurveyAnswers({
                  ...surveyAnswers,
                  [q.id]: value
                })}
                style={{ fontSize: 24 }}
              />
            )}
            
            {q.type === 'textarea' && (
              <TextArea 
                rows={4}
                value={surveyAnswers[q.id] || ''} 
                onChange={(e) => setSurveyAnswers({
                  ...surveyAnswers,
                  [q.id]: e.target.value
                })}
                placeholder="Share your thoughts..."
              />
            )}
          </div>
        ))}
      </div>
      
      <div className="survey-actions">
        <Button 
          type="primary" 
          onClick={handleSurveySubmit}
          disabled={item.questions.some(q => q.required && !surveyAnswers[q.id])}
          block
          size="large"
        >
          Submit Survey
        </Button>
      </div>
    </div>
  );

  const renderQuiz = (item) => (
    <div className="quiz-content">
      <div className="quiz-header">
        <h3>{item.title}</h3>
        <p className="quiz-description">{item.description}</p>
        <div className="passing-score">
          <span>Passing Score: <strong>{item.passingScore}%</strong></span>
        </div>
      </div>
      
      <div className="quiz-questions">
        {item.questions.map((q, index) => (
          <div key={q.id} className="quiz-question">
            <div className="question-number">Question {index + 1}</div>
            <p className="question-text">{q.question}</p>
            
            {q.type === 'multiple-choice' && (
              <Radio.Group 
                value={quizAnswers[q.id]}
                onChange={(e) => setQuizAnswers({
                  ...quizAnswers,
                  [q.id]: e.target.value
                })}
                className="quiz-options"
              >
                {q.options.map((option, optIndex) => (
                  <Radio key={optIndex} value={optIndex} className="quiz-option">
                    {option}
                  </Radio>
                ))}
              </Radio.Group>
            )}
            
            {q.type === 'multiple-select' && (
              <Checkbox.Group 
                value={quizAnswers[q.id] || []}
                onChange={(values) => setQuizAnswers({
                  ...quizAnswers,
                  [q.id]: values
                })}
                className="quiz-options"
              >
                {q.options.map((option, optIndex) => (
                  <Checkbox key={optIndex} value={optIndex} className="quiz-option">
                    {option}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            )}
          </div>
        ))}
      </div>
      
      <div className="quiz-actions">
        <Button 
          type="primary" 
          onClick={handleQuizSubmit}
          disabled={item.questions.some(q => !quizAnswers[q.id] && quizAnswers[q.id] !== 0)}
          block
          size="large"
        >
          Submit Quiz
        </Button>
      </div>
    </div>
  );

  const renderTraining = (item, options = {}) => {
    const compact = !!options.compact;
    const steps = getTrainingSteps(item);
    const totalSteps = steps.length;
    const currentStepData = steps[Math.min(Math.max(currentStep, 0), Math.max(totalSteps - 1, 0))];
    const header = item && item.header && typeof item.header === 'object' ? item.header : {};
    const headerBg = header.background || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

    const canComplete = isTrainingComplete(item);

    const renderFeedback = (step) => {
      const key = `${item.id}:${step.id}:feedback`;
      const val = surveyAnswers[key] || '';
      return (
        <div className="feedback-section">
          {step?.description && (
            <p className="step-description">{step.description}</p>
          )}
          <TextArea
            rows={4}
            value={val}
            onChange={(e) => setSurveyAnswers({ ...surveyAnswers, [key]: e.target.value })}
            placeholder="Share your feedback or thoughts..."
          />
          <div className="feedback-actions">
            <Button
              type="primary"
              onClick={() => {
                markTrainingStepComplete(item.id, step.id);
                if (onComplete) {
                  onComplete({
                    type: 'training_feedback',
                    itemId: item.id,
                    stepId: step.id,
                    value: surveyAnswers[key] || '',
                  });
                }
                Modal.success({ 
                  title: '✓ Feedback Saved', 
                  content: 'Thank you for your feedback!' 
                });
              }}
              disabled={!val.trim()}
            >
              Save Feedback
            </Button>
          </div>
        </div>
      );
    };

    const renderQuizStep = (step) => {
      const questions = Array.isArray(step?.questions) ? step.questions : [];
      const passingScore = typeof step?.passingScore === 'number' ? step.passingScore : 70;
      const stepKey = `${item.id}:${step.id}:quiz`;
      const answers = quizAnswers[stepKey] || {};

      const setAnswer = (qid, v) => {
        setQuizAnswers({
          ...quizAnswers,
          [stepKey]: {
            ...(quizAnswers[stepKey] || {}),
            [qid]: v,
          },
        });
      };

      const submit = () => {
        let correct = 0;
        questions.forEach((q) => {
          if (!q || typeof q !== 'object') return;
          const userAnswer = answers[q.id];
          if (q.type === 'multiple-choice') {
            if (userAnswer === q.correctAnswer) correct++;
          } else if (q.type === 'multiple-select') {
            const userSet = new Set(userAnswer || []);
            const correctSet = new Set(q.correctAnswers || []);
            if (userSet.size === correctSet.size && [...userSet].every((x) => correctSet.has(x))) {
              correct++;
            }
          }
        });

        const score = questions.length > 0 ? (correct / questions.length) * 100 : 0;
        const passed = score >= passingScore;
        if (passed) {
          markTrainingStepComplete(item.id, step.id);
        }

        if (onComplete) {
          onComplete({
            type: 'training_quiz',
            itemId: item.id,
            stepId: step.id,
            score,
            passed,
            answers,
          });
        }

        Modal.info({
          title: passed ? '🎉 Quiz Passed!' : '📚 Keep Learning',
          content: `You scored ${score.toFixed(0)}%. ${passed ? 'Excellent work!' : `You need ${passingScore}% to pass.`}`,
        });
      };

      return (
        <div className="quiz-step">
          <div className="passing-score-badge">
            Passing Score: {passingScore}%
          </div>
          {questions.map((q, idx) => (
            <div key={q.id || idx} className="quiz-question">
              <div className="question-number">Question {idx + 1}</div>
              <p className="question-text">{q.question}</p>
              {q.type === 'multiple-choice' && (
                <Radio.Group
                  value={answers[q.id]}
                  onChange={(e) => setAnswer(q.id, e.target.value)}
                  className="quiz-options"
                >
                  {(q.options || []).map((opt, optIndex) => (
                    <Radio key={optIndex} value={optIndex} className="quiz-option">
                      {opt}
                    </Radio>
                  ))}
                </Radio.Group>
              )}
              {q.type === 'multiple-select' && (
                <Checkbox.Group
                  value={answers[q.id] || []}
                  onChange={(vals) => setAnswer(q.id, vals)}
                  className="quiz-options"
                >
                  {(q.options || []).map((opt, optIndex) => (
                    <Checkbox key={optIndex} value={optIndex} className="quiz-option">
                      {opt}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              )}
            </div>
          ))}
          <div className="quiz-step-actions">
            <Button
              type="primary"
              onClick={submit}
              disabled={questions.some((q) => {
                const a = answers[q.id];
                return (q.type === 'multiple-choice') ? (a !== 0 && !a) : (q.type === 'multiple-select') ? (!a || a.length === 0) : false;
              })}
              block
              size="large"
            >
              Submit Quiz
            </Button>
          </div>
        </div>
      );
    };

    const renderStepBody = (step) => {
      const type = step?.type || 'content';
      if (type === 'video') return (
        <div className="video-step">
          {renderVideo(step)}
        </div>
      );
      if (type === 'feedback') return renderFeedback(step);
      if (type === 'quiz') return renderQuizStep(step);
      return (
        <div className="content-step">
          {step?.content && <p className="step-content">{step.content}</p>}
          {step?.videoUrl && renderVideo(step)}
        </div>
      );
    };

    if (compact) {
      const firstVideo = item?.videoUrl
        ? { videoUrl: item.videoUrl, title: item.title }
        : (steps.find((s) => s && s.videoUrl) || null);

      return (
        <div className="training-compact">
          <h3 className="training-title">{item.title}</h3>
          {firstVideo ? renderVideo(firstVideo, { allowExpand: false, allowFullscreen: false }) : (
            <div className="no-video">No video available</div>
          )}
        </div>
      );
    }

    return (
      <div className="training-content">
        <div className="training-header" style={{ background: headerBg }}>
          <h3 className="training-title">{item.title}</h3>
          {item.subtitle && <p className="training-subtitle">{item.subtitle}</p>}
          {item.description && <p className="training-description">{item.description}</p>}
        </div>

        {totalSteps > 1 && (
          <div className="training-progress">
            <Steps current={currentStep} size="small">
              {steps.map((step, index) => (
                <Step key={step.id || index} title={step.title} />
              ))}
            </Steps>
          </div>
        )}

        <div className="training-step-content">
          {totalSteps > 1 && (
            <h4 className="step-title">{currentStepData?.title}</h4>
          )}
          {currentStepData?.subtitle && (
            <p className="step-subtitle">{currentStepData.subtitle}</p>
          )}
          {renderStepBody(currentStepData)}
        </div>

        <div className="training-navigation">
          <Button
            disabled={currentStep === 0}
            onClick={() => setCurrentStep(currentStep - 1)}
            icon={<LeftOutlined />}
          >
            Previous
          </Button>

          <span className="step-indicator">
            {totalSteps > 1 ? `Step ${currentStep + 1} of ${totalSteps}` : 'Training'}
          </span>

          {currentStep < totalSteps - 1 ? (
            <Button
              type="primary"
              onClick={() => {
                const step = currentStepData;
                if (step && (step.type === 'video' || step.type === 'content')) {
                  markTrainingStepComplete(item.id, step.id);
                }
                setCurrentStep(currentStep + 1);
              }}
              icon={<RightOutlined />}
              iconPosition="end"
            >
              Next
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={() => {
                const step = currentStepData;
                if (step && (step.type === 'video' || step.type === 'content')) {
                  markTrainingStepComplete(item.id, step.id);
                }
                if (canComplete) {
                  handleTrainingComplete();
                } else {
                  Modal.warning({
                    title: 'Almost There!',
                    content: 'Please complete all required steps (quizzes/feedback) before finishing.',
                  });
                }
              }}
              icon={<CheckCircleOutlined />}
            >
              Complete
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!selectedItem) return null;

    switch (selectedItem.type) {
      case 'notification':
        return renderNotification(selectedItem);
      case 'survey':
        return renderSurvey(selectedItem);
      case 'quiz':
        return renderQuiz(selectedItem);
      case 'training':
        return renderTraining(selectedItem);
      default:
        return null;
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'notification':
        return <BellOutlined />;
      case 'survey':
        return <FileTextOutlined />;
      case 'quiz':
        return <QuestionCircleOutlined />;
      case 'training':
        return <PlayCircleOutlined />;
      default:
        return <BellOutlined />;
    }
  };

  const getPriorityIndicator = (priority) => {
    const colors = {
      high: '#ff4d4f',
      medium: '#faad14',
      low: '#1890ff',
    };
    return colors[priority] || '#d9d9d9';
  };

  const typeCount = (type) => {
    if (type === 'all') return unreadCount;
    return items.filter((x) => x && x.type === type && !x.read).length;
  };

  const renderWidgetPanel = (inModal) => (
    <div
      className={`widget-panel ${viewMode}${inModal ? ' modal-mode' : ''}`}
      style={inModal ? { height: '100%', maxHeight: 'none' } : undefined}
    >
      <div className="panel-header">
        <div>
          <h3>Notifications</h3>
          {unreadCount > 0 && (
            <span className="unread-count">{unreadCount} unread</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Tooltip title={inModal ? 'Exit expanded view' : 'Expand'}>
            <Button
              type="text"
              icon={inModal ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => {
                if (inModal) {
                  setIsWidgetModalOpen(false);
                  return;
                }
                setIsWidgetModalOpen(true);
                setIsExpanded(false);
              }}
              className="close-btn"
            />
          </Tooltip>
          <Tooltip title="Close">
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => {
                if (inModal) {
                  setIsWidgetModalOpen(false);
                } else {
                  setIsExpanded(false);
                }
              }}
              className="close-btn"
            />
          </Tooltip>
        </div>
      </div>

      <div className="panel-body">
        <div className="panel-content">
          {viewMode === 'slider' ? (
            <div className="slider-view">
              {getSliderItems().length === 0 ? (
                <div className="empty-state">
                  <BellOutlined />
                  <p>No notifications</p>
                </div>
              ) : (
                <>
                  <div className="slider-controls">
                    <span className="slider-counter">
                      {sliderIndex + 1} / {getSliderItems().length}
                    </span>
                    <Button size="small" onClick={closeCurrentSlide}>
                      Close
                    </Button>
                  </div>
                  <Carousel
                    ref={carouselRef}
                    dots
                    afterChange={(i) => {
                      setSliderIndex(i);
                      const it = getSliderItems()[i];
                      resetItemStateForView(it);
                    }}
                  >
                    {getSliderItems().map((it) => (
                      <div key={it.id}>
                        <div className="slider-item">
                          {(() => {
                            if (it.type === 'notification') return renderNotification(it);
                            if (it.type === 'survey') return renderSurvey(it);
                            if (it.type === 'quiz') return renderQuiz(it);
                            if (it.type === 'training') return renderTraining(it, { compact: true });
                            return renderNotification(it);
                          })()}
                        </div>
                      </div>
                    ))}
                  </Carousel>
                  <div className="slider-nav">
                    <Button
                      size="small"
                      onClick={() => carouselRef.current && carouselRef.current.prev()}
                      icon={<LeftOutlined />}
                    >
                      Previous
                    </Button>
                    <Button
                      size="small"
                      onClick={() => carouselRef.current && carouselRef.current.next()}
                      icon={<RightOutlined />}
                      iconPosition="end"
                    >
                      Next
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="list-view">
              {getFilteredItems().length === 0 ? (
                <div className="empty-state">
                  <BellOutlined />
                  <p>No notifications</p>
                </div>
              ) : (
                getFilteredItems().map(item => (
                  <div
                    key={item.id}
                    className={`notification-item ${!item.read ? 'unread' : ''}`}
                    onClick={() => handleItemClick(item)}
                  >
                    <div
                      className="priority-indicator"
                      style={{ backgroundColor: getPriorityIndicator(item.priority) }}
                    />
                    <div className="item-icon">
                      {getIcon(item.type)}
                    </div>
                    <div className="item-content">
                      <div className="item-header">
                        <h4>{item.title}</h4>
                        {item.required && (
                          <span className="required-tag">Required</span>
                        )}
                      </div>
                      <p>{item.description || item.message}</p>
                      {!item.read && <div className="unread-dot" />}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        <div className="panel-tabs">
          <button
            type="button"
            className={`tab-btn ${activeType === 'all' ? 'active' : ''}`}
            onClick={() => {
              setActiveType('all');
              setSliderIndex(0);
            }}
          >
            <Badge count={typeCount('all')} overflowCount={99} size="small">
              <BellOutlined />
            </Badge>
          </button>
          <button
            type="button"
            className={`tab-btn ${activeType === 'notification' ? 'active' : ''}`}
            onClick={() => {
              setActiveType('notification');
              setSliderIndex(0);
            }}
          >
            <Badge count={typeCount('notification')} overflowCount={99} size="small">
              <BellOutlined />
            </Badge>
          </button>
          <button
            type="button"
            className={`tab-btn ${activeType === 'training' ? 'active' : ''}`}
            onClick={() => {
              setActiveType('training');
              setSliderIndex(0);
            }}
          >
            <Badge count={typeCount('training')} overflowCount={99} size="small">
              <PlayCircleOutlined />
            </Badge>
          </button>
          <button
            type="button"
            className={`tab-btn ${activeType === 'quiz' ? 'active' : ''}`}
            onClick={() => {
              setActiveType('quiz');
              setSliderIndex(0);
            }}
          >
            <Badge count={typeCount('quiz')} overflowCount={99} size="small">
              <QuestionCircleOutlined />
            </Badge>
          </button>
          <button
            type="button"
            className={`tab-btn ${activeType === 'survey' ? 'active' : ''}`}
            onClick={() => {
              setActiveType('survey');
              setSliderIndex(0);
            }}
          >
            <Badge count={typeCount('survey')} overflowCount={99} size="small">
              <FileTextOutlined />
            </Badge>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Floating Widget */}
      <div 
        className={`notification-widget ${position} ${isExpanded ? 'expanded' : ''}`}
      >
        {/* Tab Button */}
        <div 
          className="widget-trigger"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="avatar-container">
            <Avatar src={avataricon} size={44} className="avatar-icon" />
            <div className={`notification-pulse ${unreadCount > 0 ? 'active' : ''}`}>
              <Badge count={unreadCount} overflowCount={99} offset={[0, 0]} size="small">
                <BellOutlined style={{ color: '#fff', fontSize: 14 }} />
              </Badge>
            </div>
          </div>
          {requiredCount > 0 && (
            <div className="required-badge">
              {requiredCount} Required
            </div>
          )}
        </div>

        {/* Preview Chat Bubble */}
        {showPreview && previewMessage && !isExpanded && (
          <div 
            className={`preview-chat-bubble ${position}`}
            onClick={() => setIsExpanded(true)}
          >
            <div className="preview-header">
              <div className="preview-icon">
                {getIcon(previewMessage.type)}
              </div>
              <button 
                className="preview-close"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPreview(false);
                }}
              >
                <CloseOutlined />
              </button>
            </div>
            <div className="preview-content">
              <h4>{previewMessage.title}</h4>
              <p>{previewMessage.description}</p>
            </div>
            <div className="preview-tail"></div>
          </div>
        )}

        {/* Expanded Panel */}
        {isExpanded && !isWidgetModalOpen ? renderWidgetPanel(false) : null}
      </div>

      <Modal
        open={isWidgetModalOpen}
        onCancel={() => setIsWidgetModalOpen(false)}
        footer={null}
        width={800}
        style={{ top: 24 }}
        bodyStyle={{ padding: 0, height: 800 }}
        destroyOnClose
      >
        <div style={{ height: 640 }}>
          {renderWidgetPanel(true)}
        </div>
      </Modal>

      {/* Modal for Content */}
      {viewMode === 'list' ? (
        <Modal
          open={showModal}
          onCancel={handleClose}
          footer={null}
          width={800}
          closable={!selectedItem?.required}
          maskClosable={!selectedItem?.required}
          className="notification-modal"
        >
          {renderContent()}
        </Modal>
      ) : null}
    </>
  );
};

export default NotificationWidget;